import { AnimatePresence } from 'framer-motion';
import { useEffect, useRef, type ReactNode } from 'react';
import { useCanAddToCalendar } from '@/features/interactions/addToCalendar/hooks/useCanAddToCalendar';
import { useActiveView } from '@/features/planner/hooks/useActiveView';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { useIsMobileBreakpoint } from '@/hooks/useIsBreakpoint';
import { InteractionAppNames } from '@/types/gql.generated';
import { Flex, MotionBox, MotionFlex } from '@/ui';
import { usePageExperienceContext } from '../../contexts/PageExperienceContext';
import { useCustomizeMode } from '../CustomizeMode';
import { ExportCalendarSidebarBody } from './ExportCalendarSidebarBody';
import { RAIL_OFFSET_VAR, useRailOffset } from './useRailOffset';

const SIDEBAR_WIDTH = 400;
const ACTION = InteractionAppNames.AddToCalendar;

export const ExportCalendarSidebar = () => {
  const isMobile = useIsMobileBreakpoint();
  const { action, setAction } = useCustomizeMode();
  const { experience } = usePageExperienceContext();
  const { activeChildId } = useActiveView(experience);
  const activeChild = experience.children.find((c) => c.id === activeChildId);
  const panelRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const isActive = action === ACTION;
  // The toggle that opens this withholds itself outside the configured
  // audience; `?action=ADD_TO_CALENDAR` is a URL away, so the panel checks too.
  const canAddToCalendar = useCanAddToCalendar();

  const show = Boolean(activeChild) && isActive && canAddToCalendar;
  useRailOffset(railRef, show && !isMobile);

  // The mobile panel is a full-screen modal overlay, so give it dialog behavior
  // (mirrors InteractionDrawer): move focus into the panel when it opens and
  // close it on Escape. The desktop rail is an in-flow surface that pushes the
  // page rather than covering it, so it stays a plain region.
  useEffect(() => {
    if (!show || !isMobile) return;
    panelRef.current?.focus();
  }, [show, isMobile]);

  useEffect(() => {
    if (!show || !isMobile) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAction(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, isMobile, setAction]);

  let panel: ReactNode = null;
  if (show && activeChild) {
    const content = (
      <ExportCalendarSidebarBody onClose={() => setAction(null)} />
    );

    // Desktop: an in-flow rail that pushes the page content left as it expands
    // (mirrors CustomizeSidebar). Mobile: a fixed full-screen overlay, since a
    // 400px rail would leave no room for the calendar.
    panel = isMobile ? (
      <MotionBox
        animate={{ x: 0 }}
        aria-label="Send to calendar"
        aria-modal="true"
        bg="white"
        borderLeftColor="line.DEFAULT"
        borderLeftStyle="solid"
        borderLeftWidth="1px"
        bottom={0}
        boxShadow="lg"
        display="flex"
        exit={{ x: '100%' }}
        flexDirection="column"
        initial={{ x: '100%' }}
        key="overlay"
        pos="fixed"
        ref={panelRef}
        right={0}
        role="dialog"
        sx={{ ...printVisibility.hide() }}
        tabIndex={-1}
        top={0}
        transition={{ type: 'tween', duration: 0.2 }}
        width="100vw"
        // Above app chrome, below portaled menus (dropdown z-index 1000).
        zIndex={100}
      >
        {content}
      </MotionBox>
    ) : (
      <MotionFlex
        alignSelf="flex-start"
        animate={{ width: SIDEBAR_WIDTH }}
        exit={{ width: 0 }}
        flexShrink={0}
        // Offset kept live by useRailOffset.
        h={`calc(100vh - var(${RAIL_OFFSET_VAR}, 0px))`}
        initial={{ width: 0 }}
        key="rail"
        overflow="hidden"
        position="sticky"
        ref={railRef}
        sx={{ ...printVisibility.hide() }}
        top={`var(${RAIL_OFFSET_VAR}, 0px)`}
        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
      >
        {/* Fixed-width inner so the body is laid out once at the rail's resting
            width and simply revealed as the outer width animates, rather than
            reflowing through every intermediate width. Must not be a flex item
            that grows: `flex={1}` sets `flex-basis: 0%`, which overrides `w` and
            makes the body track the animation. */}
        <Flex
          bg="white"
          borderLeftColor="line.DEFAULT"
          borderLeftStyle="solid"
          borderLeftWidth="1px"
          direction="column"
          flexShrink={0}
          minH={0}
          w={`${SIDEBAR_WIDTH}px`}
        >
          {content}
        </Flex>
      </MotionFlex>
    );
  }

  return <AnimatePresence>{panel}</AnimatePresence>;
};
